import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';

import SafeIcon from '../common/SafeIcon';
import { useClient } from '../context/ClientContext';
import { invoiceService } from '../services/invoiceService';

const {
  FiAlertCircle,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiExternalLink,
  FiFileText,
  FiFilter,
  FiLoader,
  FiMoreVertical,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiSend,
  FiTrash2,
  FiX
} = FiIcons;

const tabs = [
  'All',
  'Draft',
  'Unpaid',
  'Overdue',
  'Paid'
];

/*
 * ---------------------------------------------------------
 * MONEY
 * ---------------------------------------------------------
 */

const formatAmount = (
  value,
  currency = 'USD'
) => {
  const amount = Number(value || 0) / 100;

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

/*
 * ---------------------------------------------------------
 * STATUS
 * ---------------------------------------------------------
 */

const getDisplayStatus = (invoice) => {
  const status = String(
    invoice?.status || 'draft'
  ).toLowerCase();

  if (
    status === 'paid' ||
    status === 'void' ||
    status === 'uncollectible' ||
    status === 'canceled'
  ) {
    return status;
  }

  if (status === 'draft') {
    return 'draft';
  }

  if (
    invoice?.due_date &&
    new Date(`${invoice.due_date}T23:59:59`) < new Date() &&
    Number(invoice?.amount_due || 0) > 0
  ) {
    return 'overdue';
  }

  if (
    status === 'open' ||
    status === 'sent' ||
    status === 'unpaid'
  ) {
    return 'unpaid';
  }

  return status;
};

const canResendInvoice = (invoice) => {
  const status = String(
    invoice?.status || ''
  ).toLowerCase();

  return (
    ['open', 'sent', 'unpaid'].includes(status) &&
    Boolean(invoice?.stripe_invoice_id) &&
    Boolean(invoice?.customer?.email) &&
    Number(invoice?.amount_due || 0) > 0
  );
};

function StatusBadge({ invoice }) {
  const status = getDisplayStatus(invoice);

  const styles = {
    paid:
      'border-emerald-200 bg-emerald-50 text-emerald-700',
    overdue:
      'border-rose-200 bg-rose-50 text-rose-700',
    unpaid:
      'border-blue-200 bg-blue-50 text-blue-700',
    open:
      'border-blue-200 bg-blue-50 text-blue-700',
    draft:
      'border-slate-200 bg-slate-50 text-slate-600',
    void:
      'border-slate-200 bg-slate-50 text-slate-500',
    uncollectible:
      'border-amber-200 bg-amber-50 text-amber-700',
    canceled:
      'border-slate-200 bg-slate-50 text-slate-500'
  };

  return (
    <span
      className={`inline-flex rounded-md border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide ${
        styles[status] || styles.draft
      }`}
    >
      {status}
    </span>
  );
}

/*
 * ---------------------------------------------------------
 * SUMMARY CARD
 * ---------------------------------------------------------
 */

function SummaryCard({
  icon,
  label,
  amount,
  count,
  tone,
  currency
}) {
  const tones = {
    rose: {
      icon: 'text-rose-500',
      count: 'bg-rose-50 text-rose-600'
    },
    blue: {
      icon: 'text-blue-500',
      count: 'bg-blue-50 text-blue-600'
    },
    emerald: {
      icon: 'text-emerald-500',
      count: 'bg-emerald-50 text-emerald-600'
    }
  };

  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
      <div>
        <div className="mb-1.5 flex items-center gap-2">
          <SafeIcon
            icon={icon}
            className={`text-sm ${tones[tone].icon}`}
          />

          <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
            {label}
          </span>
        </div>

        <p className="text-lg font-medium tracking-tight text-slate-900">
          {formatAmount(amount, currency)}
        </p>
      </div>

      <span
        className={`flex h-9 min-w-9 items-center justify-center rounded-lg px-2 text-xs font-medium ${tones[tone].count}`}
      >
        {count}
      </span>
    </div>
  );
}

/*
 * ---------------------------------------------------------
 * INVOICES PAGE
 * ---------------------------------------------------------
 */

export default function Invoices() {
  const navigate = useNavigate();
  const { activeClient } = useClient();

  const organizationId = activeClient?.id;

  const defaultCurrency = String(
    activeClient?.currency ||
    activeClient?.billingCurrency ||
    'USD'
  ).toUpperCase();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [sendingInvoiceId, setSendingInvoiceId] =
    useState(null);

  const [sendModalInvoice, setSendModalInvoice] =
    useState(null);

  const [resendingInvoiceId, setResendingInvoiceId] =
    useState(null);

  const [resendModalInvoice, setResendModalInvoice] =
    useState(null);

  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [activeTab, setActiveTab] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const [openActionMenu, setOpenActionMenu] =
    useState(null);

  const [
    actionMenuPosition,
    setActionMenuPosition
  ] = useState({
    top: 0,
    left: 0
  });

  const actionMenuRef = useRef(null);
  const resendInProgressRef = useRef(false);

  const busy = Boolean(
    sendingInvoiceId || resendingInvoiceId
  );

  /*
   * -------------------------------------------------------
   * FETCH
   * -------------------------------------------------------
   */

  const fetchInvoices = async ({
    silent = false
  } = {}) => {
    if (!organizationId) {
      setInvoices([]);
      setLoading(false);
      setRefreshing(false);
      return;
    }

    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError(null);

      const data = await invoiceService.getInvoices(
        organizationId
      );

      setInvoices(data || []);
    } catch (fetchError) {
      console.error(
        'Error fetching invoices:',
        fetchError
      );

      setInvoices([]);

      setError(
        fetchError?.message ||
        'Unable to load invoices.'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [organizationId]);

  /*
   * -------------------------------------------------------
   * CLOSE ACTION MENU
   * -------------------------------------------------------
   */

  useEffect(() => {
    if (!openActionMenu) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setOpenActionMenu(null);
      }
    };

    const handleWindowChange = () => {
      setOpenActionMenu(null);
    };

    document.addEventListener(
      'mousedown',
      handleClickOutside
    );

    window.addEventListener(
      'resize',
      handleWindowChange
    );

    window.addEventListener(
      'scroll',
      handleWindowChange,
      true
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside
      );

      window.removeEventListener(
        'resize',
        handleWindowChange
      );

      window.removeEventListener(
        'scroll',
        handleWindowChange,
        true
      );
    };
  }, [openActionMenu]);

  /*
   * -------------------------------------------------------
   * MODAL KEYBOARD / SCROLL
   * -------------------------------------------------------
   */

  useEffect(() => {
    if (!sendModalInvoice && !resendModalInvoice) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (
        event.key === 'Escape' &&
        !busy
      ) {
        setSendModalInvoice(null);
        setResendModalInvoice(null);
      }
    };

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    document.addEventListener(
      'keydown',
      handleKeyDown
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      document.removeEventListener(
        'keydown',
        handleKeyDown
      );
    };
  }, [
    sendModalInvoice,
    resendModalInvoice,
    busy
  ]);

  /*
   * -------------------------------------------------------
   * SUMMARY
   * -------------------------------------------------------
   */

  const stats = useMemo(() => {
    return invoices.reduce(
      (acc, invoice) => {
        const status = getDisplayStatus(invoice);

        const amountDue = Number(
          invoice.amount_due || 0
        );

        const amountPaid = Number(
          invoice.amount_paid || 0
        );

        if (status === 'overdue') {
          acc.overdue.amount += amountDue;
          acc.overdue.count += 1;
        }

        if (
          status === 'unpaid' ||
          status === 'overdue'
        ) {
          acc.outstanding.amount += amountDue;
          acc.outstanding.count += 1;
        }

        if (status === 'paid') {
          acc.paid.amount +=
            amountPaid ||
            Number(invoice.total_amount || 0);

          acc.paid.count += 1;
        }

        return acc;
      },
      {
        overdue: {
          amount: 0,
          count: 0
        },
        outstanding: {
          amount: 0,
          count: 0
        },
        paid: {
          amount: 0,
          count: 0
        }
      }
    );
  }, [invoices]);

  /*
   * -------------------------------------------------------
   * FILTER
   * -------------------------------------------------------
   */

  const filteredInvoices = useMemo(() => {
    const search = searchQuery
      .trim()
      .toLowerCase();

    return invoices.filter((invoice) => {
      const status = getDisplayStatus(invoice);

      let matchesTab = activeTab === 'All';

      if (activeTab === 'Draft') {
        matchesTab = status === 'draft';
      }

      if (activeTab === 'Unpaid') {
        matchesTab = status === 'unpaid';
      }

      if (activeTab === 'Overdue') {
        matchesTab = status === 'overdue';
      }

      if (activeTab === 'Paid') {
        matchesTab = status === 'paid';
      }

      if (!matchesTab) {
        return false;
      }

      if (!search) {
        return true;
      }

      const customerName = String(
        invoice.customer?.name || ''
      ).toLowerCase();

      const customerCompany = String(
        invoice.customer?.company_name || ''
      ).toLowerCase();

      const customerEmail = String(
        invoice.customer?.email || ''
      ).toLowerCase();

      const invoiceNumber = String(
        invoice.invoice_number || ''
      ).toLowerCase();

      return (
        customerName.includes(search) ||
        customerCompany.includes(search) ||
        customerEmail.includes(search) ||
        invoiceNumber.includes(search)
      );
    });
  }, [
    activeTab,
    invoices,
    searchQuery
  ]);

  /*
   * -------------------------------------------------------
   * CREATE
   * -------------------------------------------------------
   */

  const handleCreate = () => {
    navigate('/sales/invoices/new');
  };

  /*
   * -------------------------------------------------------
   * ACTION MENU
   * -------------------------------------------------------
   */

  const handleActionMenu = (event, invoiceId) => {
    event.stopPropagation();

    if (openActionMenu === invoiceId) {
      setOpenActionMenu(null);
      return;
    }

    const invoice = invoices.find(
      (item) => item.id === invoiceId
    );

    const rect =
      event.currentTarget.getBoundingClientRect();

    const menuWidth = 208;
    const menuHeight =
      invoice && canResendInvoice(invoice)
        ? 210
        : 170;

    const gap = 6;

    let left = rect.right - menuWidth;
    let top = rect.bottom + gap;

    if (left < 12) {
      left = 12;
    }

    if (left + menuWidth > window.innerWidth - 12) {
      left = window.innerWidth - menuWidth - 12;
    }

    if (top + menuHeight > window.innerHeight - 12) {
      top = rect.top - menuHeight - gap;
    }

    if (top < 12) {
      top = 12;
    }

    setActionMenuPosition({
      top,
      left
    });

    setOpenActionMenu(invoiceId);
  };

  /*
   * -------------------------------------------------------
   * OPEN SEND MODAL
   * -------------------------------------------------------
   */

  const handleOpenSendModal = (invoice) => {
    setOpenActionMenu(null);

    if (
      invoice.status !== 'draft' ||
      busy
    ) {
      return;
    }

    if (!invoice.customer?.email) {
      window.alert(
        'This customer does not have an email address.'
      );
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setSendModalInvoice(invoice);
  };

  const handleCloseSendModal = () => {
    if (busy) {
      return;
    }

    setSendModalInvoice(null);
  };

  /*
   * -------------------------------------------------------
   * SEND INVOICE THROUGH STRIPE
   * -------------------------------------------------------
   */

  const handleConfirmSendInvoice = async () => {
    const invoice = sendModalInvoice;

    if (
      !invoice ||
      !organizationId ||
      invoice.status !== 'draft' ||
      busy
    ) {
      return;
    }

    try {
      setSendingInvoiceId(invoice.id);
      setError(null);
      setSuccessMessage(null);

      const updatedInvoice =
        await invoiceService.sendInvoice(
          organizationId,
          invoice.id
        );

      setInvoices((current) =>
        current.map((item) =>
          item.id === invoice.id
            ? updatedInvoice
            : item
        )
      );

      setSendModalInvoice(null);

      setSuccessMessage(
        `Invoice ${invoice.invoice_number} was finalized successfully.`
      );
    } catch (sendError) {
      console.error(
        'Send invoice error:',
        sendError
      );

      const message =
        sendError?.message ||
        'Unable to send invoice.';

      setError(message);

      try {
        await fetchInvoices({
          silent: true
        });
      } catch (refreshError) {
        console.error(
          'Unable to refresh invoices:',
          refreshError
        );
      }

      window.alert(message);
    } finally {
      setSendingInvoiceId(null);
    }
  };

  /*
   * -------------------------------------------------------
   * RESEND INVOICE EMAIL
   *
   * Uses existing Stripe invoice.
   * Does not create another invoice or payment.
   * -------------------------------------------------------
   */

  const handleOpenResendModal = (invoice) => {
    setOpenActionMenu(null);

    if (
      !canResendInvoice(invoice) ||
      busy
    ) {
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setResendModalInvoice(invoice);
  };

  const handleCloseResendModal = () => {
    if (
      busy ||
      resendInProgressRef.current
    ) {
      return;
    }

    setResendModalInvoice(null);
  };

  const handleConfirmResendInvoice = async () => {
    const invoice = resendModalInvoice;

    if (
      !invoice ||
      !organizationId ||
      !canResendInvoice(invoice) ||
      busy ||
      resendInProgressRef.current
    ) {
      return;
    }

    resendInProgressRef.current = true;

    try {
      setResendingInvoiceId(invoice.id);
      setError(null);
      setSuccessMessage(null);

      const result =
        await invoiceService.resendInvoiceEmail(
          organizationId,
          invoice.id
        );

      setResendModalInvoice(null);

      setSuccessMessage(
        `Invoice ${invoice.invoice_number} reminder submitted to SendGrid for ${
          result?.customerEmail ||
          invoice.customer.email
        }.`
      );
    } catch (resendError) {
      console.error(
        '[Invoices] Resend email failed:',
        resendError
      );

      const message =
        resendError?.message ||
        'Unable to resend invoice email.';

      setError(message);
      window.alert(message);
    } finally {
      resendInProgressRef.current = false;
      setResendingInvoiceId(null);
    }
  };

  /*
   * -------------------------------------------------------
   * DELETE DRAFT
   * -------------------------------------------------------
   */

  const handleDelete = async (invoice) => {
    setOpenActionMenu(null);

    if (busy) {
      return;
    }

    if (invoice.status !== 'draft') {
      window.alert(
        'Only draft invoices can be deleted.'
      );
      return;
    }

    const confirmed = window.confirm(
      `Delete invoice ${invoice.invoice_number}? This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      await invoiceService.deleteInvoice(
        organizationId,
        invoice.id
      );

      setInvoices((current) =>
        current.filter(
          (item) => item.id !== invoice.id
        )
      );

      setSuccessMessage(
        `Invoice ${invoice.invoice_number} was deleted.`
      );
    } catch (deleteError) {
      console.error(
        'Delete invoice error:',
        deleteError
      );

      window.alert(
        deleteError?.message ||
        'Unable to delete invoice.'
      );
    }
  };

  /*
   * -------------------------------------------------------
   * OPEN STRIPE INVOICE
   * -------------------------------------------------------
   */

  const handleOpenInvoice = (invoice) => {
    setOpenActionMenu(null);

    if (!invoice.hosted_invoice_url) {
      return;
    }

    window.open(
      invoice.hosted_invoice_url,
      '_blank',
      'noopener,noreferrer'
    );
  };

  /*
   * -------------------------------------------------------
   * PDF / PRINT
   * -------------------------------------------------------
   */

  const handleDownloadPDF = (invoice) => {
    setOpenActionMenu(null);

    if (invoice.invoice_pdf_url) {
      window.open(
        invoice.invoice_pdf_url,
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }

    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      window.alert(
        'Please allow popups to print this invoice.'
      );
      return;
    }

    const invoiceDate = invoice.issue_date
      ? format(
          new Date(`${invoice.issue_date}T00:00:00`),
          'MMM dd, yyyy'
        )
      : 'N/A';

    const dueDate = invoice.due_date
      ? format(
          new Date(`${invoice.due_date}T00:00:00`),
          'MMM dd, yyyy'
        )
      : 'N/A';

    const companyName =
      activeClient?.legal_name ||
      activeClient?.name ||
      'Your Company';

    const customerName =
      invoice.customer?.name ||
      'Customer';

    const customerEmail =
      invoice.customer?.email ||
      '';

    const currency =
      invoice.currency ||
      defaultCurrency;

    const escapeHtml = (value) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const itemRows = (invoice.items || [])
      .map((item) => {
        return `
          <tr>
            <td>${escapeHtml(item.description || 'Item')}</td>
            <td>${Number(item.quantity || 0)}</td>
            <td>${formatAmount(item.unit_amount, currency)}</td>
            <td>${formatAmount(item.amount, currency)}</td>
          </tr>
        `;
      })
      .join('');

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Invoice ${escapeHtml(invoice.invoice_number)}</title>

          <style>
            body {
              color: #1e293b;
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 42px;
            }

            .header {
              align-items: flex-start;
              border-bottom: 1px solid #e2e8f0;
              display: flex;
              justify-content: space-between;
              padding-bottom: 22px;
            }

            .title {
              font-size: 30px;
              font-weight: 600;
              letter-spacing: -1px;
            }

            .muted {
              color: #64748b;
              font-size: 13px;
            }

            .details {
              display: flex;
              justify-content: space-between;
              margin: 34px 0;
            }

            .label {
              color: #64748b;
              font-size: 11px;
              letter-spacing: .08em;
              text-transform: uppercase;
            }

            .value {
              font-size: 15px;
              font-weight: 600;
              margin-top: 6px;
            }

            table {
              border-collapse: collapse;
              width: 100%;
            }

            th {
              background: #f8fafc;
              color: #64748b;
              font-size: 11px;
              font-weight: 600;
              letter-spacing: .08em;
              padding: 12px;
              text-align: left;
              text-transform: uppercase;
            }

            td {
              border-bottom: 1px solid #e2e8f0;
              font-size: 14px;
              padding: 14px 12px;
            }

            td:nth-child(n+2),
            th:nth-child(n+2) {
              text-align: right;
            }

            .summary {
              margin-left: auto;
              margin-top: 28px;
              width: 320px;
            }

            .summary-row {
              display: flex;
              justify-content: space-between;
              padding: 7px 0;
            }

            .total {
              border-top: 2px solid #1e293b;
              font-size: 17px;
              font-weight: 700;
              margin-top: 8px;
              padding-top: 14px;
            }

            .notes {
              border-top: 1px solid #e2e8f0;
              margin-top: 42px;
              padding-top: 22px;
            }
          </style>
        </head>

        <body>
          <div class="header">
            <div>
              <div class="title">Invoice</div>

              <div class="muted" style="margin-top: 6px;">
                #${escapeHtml(invoice.invoice_number)}
              </div>
            </div>

            <div style="font-size: 20px; font-weight: 600;">
              ${escapeHtml(companyName)}
            </div>
          </div>

          <div class="details">
            <div>
              <div class="label">Bill To</div>
              <div class="value">${escapeHtml(customerName)}</div>

              <div class="muted" style="margin-top: 4px;">
                ${escapeHtml(customerEmail)}
              </div>
            </div>

            <div style="display: flex; gap: 40px;">
              <div>
                <div class="label">Issue Date</div>
                <div class="value">${invoiceDate}</div>
              </div>

              <div>
                <div class="label">Due Date</div>
                <div class="value">${dueDate}</div>
              </div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>

            <tbody>${itemRows}</tbody>
          </table>

          <div class="summary">
            <div class="summary-row">
              <span>Subtotal</span>
              <span>${formatAmount(invoice.subtotal, currency)}</span>
            </div>

            <div class="summary-row">
              <span>Tax</span>
              <span>${formatAmount(invoice.tax_amount, currency)}</span>
            </div>

            <div class="summary-row">
              <span>Discount</span>
              <span>-${formatAmount(invoice.discount_amount, currency)}</span>
            </div>

            <div class="summary-row total">
              <span>Total</span>
              <span>${formatAmount(invoice.total_amount, currency)}</span>
            </div>
          </div>

          ${
            invoice.memo
              ? `
                <div class="notes">
                  <div class="label">Memo</div>
                  <div style="margin-top: 8px; font-size: 13px;">
                    ${escapeHtml(invoice.memo)}
                  </div>
                </div>
              `
              : ''
          }

          ${
            invoice.footer
              ? `
                <div class="muted" style="margin-top: 28px;">
                  ${escapeHtml(invoice.footer)}
                </div>
              `
              : ''
          }
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  /*
   * -------------------------------------------------------
   * NO ACTIVE CLIENT
   * -------------------------------------------------------
   */

  if (!organizationId) {
    return (
      <div className="mx-auto max-w-[1500px] pb-5">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <SafeIcon
              icon={FiAlertTriangle}
              className="mt-0.5 text-amber-600"
            />

            <div>
              <p className="text-sm font-semibold text-amber-900">
                No organization selected
              </p>

              <p className="mt-1 text-xs text-amber-700">
                Select a client organization to view its invoices.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /*
   * -------------------------------------------------------
   * PAGE
   * -------------------------------------------------------
   */

  return (
    <div className="mx-auto max-w-[1500px] pb-8">

      {/* HEADER */}

      <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">
            Invoices
          </h1>

          <p className="mt-1 text-xs text-slate-500">
            Manage and track invoices for{' '}
            <span className="font-medium text-slate-700">
              {activeClient?.name || 'this organization'}
            </span>
            .
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() =>
              fetchInvoices({ silent: true })
            }
            disabled={refreshing || busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-xs font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SafeIcon
              icon={refreshing ? FiLoader : FiRefreshCw}
              className={refreshing ? 'animate-spin' : ''}
            />
            Refresh
          </button>

          <button
            type="button"
            onClick={handleCreate}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-xs font-medium text-white shadow-sm transition hover:bg-slate-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SafeIcon
              icon={FiPlus}
              className="text-sm"
            />
            Create Invoice
          </button>
        </div>
      </div>

      {/* LOADING */}

      {loading ? (
        <div className="flex min-h-[300px] flex-col items-center justify-center rounded-xl border border-slate-200 bg-white">
          <SafeIcon
            icon={FiLoader}
            className="mb-2 animate-spin text-2xl text-blue-600"
          />

          <p className="text-xs text-slate-500">
            Loading invoices...
          </p>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-5"
        >
          {/* ERROR */}

          {error && (
            <div className="flex items-center gap-3 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2.5 text-xs text-rose-700">
              <SafeIcon icon={FiAlertTriangle} />

              <span className="flex-1">
                {error}
              </span>

              <button
                type="button"
                onClick={() => fetchInvoices()}
                className="ml-auto font-medium underline"
              >
                Retry
              </button>
            </div>
          )}

          {/* SUCCESS */}

          {successMessage && (
            <div className="flex items-center gap-3 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-700">
              <SafeIcon icon={FiCheckCircle} />

              <span className="flex-1">
                {successMessage}
              </span>

              <button
                type="button"
                onClick={() => setSuccessMessage(null)}
                aria-label="Dismiss notification"
                className="text-emerald-600 hover:text-emerald-900"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>
          )}

          {/* SUMMARY */}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <SummaryCard
              icon={FiAlertCircle}
              label="Overdue"
              amount={stats.overdue.amount}
              count={stats.overdue.count}
              tone="rose"
              currency={defaultCurrency}
            />

            <SummaryCard
              icon={FiClock}
              label="Outstanding"
              amount={stats.outstanding.amount}
              count={stats.outstanding.count}
              tone="blue"
              currency={defaultCurrency}
            />

            <SummaryCard
              icon={FiCheckCircle}
              label="Paid · All time"
              amount={stats.paid.amount}
              count={stats.paid.count}
              tone="emerald"
              currency={defaultCurrency}
            />
          </div>

          {/* INVOICE CARD */}

          <section className="rounded-xl border border-slate-200 bg-white shadow-sm">

            {/* FILTER BAR */}

            <div className="flex flex-col justify-between gap-3 border-b border-slate-200 px-5 py-4 lg:flex-row lg:items-center">
              <div className="flex max-w-full gap-1 overflow-x-auto rounded-lg bg-slate-50 p-1">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => {
                      setActiveTab(tab);
                      setOpenActionMenu(null);
                    }}
                    className={`whitespace-nowrap rounded-md px-3 py-1.5 text-[11px] font-medium transition ${
                      activeTab === tab
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <div className="flex w-full items-center gap-2 lg:w-auto">
                <div className="relative min-w-0 flex-1 lg:w-72">
                  <SafeIcon
                    icon={FiSearch}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400"
                  />

                  <input
                    type="text"
                    placeholder="Search invoice or customer"
                    value={searchQuery}
                    onChange={(event) => {
                      setSearchQuery(event.target.value);
                      setOpenActionMenu(null);
                    }}
                    className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-xs text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
                  />
                </div>

                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  <SafeIcon icon={FiFilter} />

                  <span className="hidden sm:inline">
                    Filter
                  </span>
                </button>
              </div>
            </div>

            {/* TABLE */}

            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[950px] border-collapse text-left">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
                    <th className="px-5 py-3">
                      Status
                    </th>

                    <th className="px-4 py-3">
                      Date / Due
                    </th>

                    <th className="px-4 py-3">
                      Invoice No.
                    </th>

                    <th className="px-4 py-3">
                      Customer
                    </th>

                    <th className="px-4 py-3 text-right">
                      Amount Due
                    </th>

                    <th className="px-4 py-3 text-right">
                      Total
                    </th>

                    <th className="w-[72px] px-4 py-3 text-center">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="text-xs">
                  {filteredInvoices.length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="px-4 py-14 text-center"
                      >
                        <div className="flex flex-col items-center">
                          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-slate-50">
                            <SafeIcon
                              icon={FiFileText}
                              className="text-xl text-slate-300"
                            />
                          </div>

                          <p className="text-sm font-medium text-slate-800">
                            No invoices found
                          </p>

                          <p className="mt-1 max-w-xs text-xs text-slate-400">
                            {invoices.length === 0
                              ? 'Create your first invoice to begin tracking customer payments.'
                              : 'No invoices match the current filters.'}
                          </p>

                          {invoices.length === 0 && (
                            <button
                              type="button"
                              onClick={handleCreate}
                              className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white transition hover:bg-slate-800"
                            >
                              <SafeIcon icon={FiPlus} />
                              Create Invoice
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredInvoices.map((invoice) => {
                      const currency =
                        invoice.currency ||
                        defaultCurrency;

                      return (
                        <tr
                          key={invoice.id}
                          className="border-b border-slate-100 last:border-b-0 transition-colors hover:bg-slate-50/70"
                        >
                          <td className="whitespace-nowrap px-5 py-4">
                            <StatusBadge invoice={invoice} />
                          </td>

                          <td className="whitespace-nowrap px-4 py-4">
                            <p className="text-slate-700">
                              {invoice.issue_date
                                ? format(
                                    new Date(
                                      `${invoice.issue_date}T00:00:00`
                                    ),
                                    'MMM dd, yyyy'
                                  )
                                : 'N/A'}
                            </p>

                            <p className="mt-1 text-[11px] text-slate-400">
                              Due{' '}
                              {invoice.due_date
                                ? format(
                                    new Date(
                                      `${invoice.due_date}T00:00:00`
                                    ),
                                    'MMM dd, yyyy'
                                  )
                                : 'N/A'}
                            </p>
                          </td>

                          <td className="whitespace-nowrap px-4 py-4">
                            <p className="font-semibold text-slate-800">
                              {invoice.invoice_number}
                            </p>

                            {invoice.stripe_invoice_id && (
                              <p className="mt-1 max-w-[160px] truncate text-[10px] text-slate-400">
                                Stripe:{' '}
                                {invoice.stripe_invoice_id}
                              </p>
                            )}
                          </td>

                          <td className="px-4 py-4">
                            <div className="min-w-[160px]">
                              <p className="font-medium text-slate-800">
                                {invoice.customer?.name ||
                                  'Unknown Customer'}
                              </p>

                              {invoice.customer?.email && (
                                <p className="mt-1 max-w-[220px] truncate text-[11px] text-slate-400">
                                  {invoice.customer.email}
                                </p>
                              )}
                            </div>
                          </td>

                          <td className="whitespace-nowrap px-4 py-4 text-right font-semibold text-slate-800">
                            {formatAmount(
                              invoice.amount_due,
                              currency
                            )}
                          </td>

                          <td className="whitespace-nowrap px-4 py-4 text-right font-semibold text-slate-800">
                            {formatAmount(
                              invoice.total_amount,
                              currency
                            )}
                          </td>

                          <td className="px-4 py-4 text-center">
                            <button
                              type="button"
                              aria-label={`Actions for invoice ${invoice.invoice_number}`}
                              onClick={(event) =>
                                handleActionMenu(
                                  event,
                                  invoice.id
                                )
                              }
                              disabled={busy}
                              className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition disabled:cursor-not-allowed disabled:opacity-50 ${
                                openActionMenu === invoice.id
                                  ? 'bg-slate-100 text-slate-700'
                                  : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
                              }`}
                            >
                              {sendingInvoiceId === invoice.id ||
                              resendingInvoiceId === invoice.id ? (
                                <SafeIcon
                                  icon={FiLoader}
                                  className="animate-spin"
                                />
                              ) : (
                                <SafeIcon icon={FiMoreVertical} />
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* FOOTER */}

            <div className="flex flex-col justify-between gap-2 border-t border-slate-200 bg-slate-50/60 px-5 py-3 sm:flex-row sm:items-center">
              <p className="text-[11px] text-slate-500">
                Showing {filteredInvoices.length} of{' '}
                {invoices.length}{' '}
                {invoices.length === 1
                  ? 'invoice'
                  : 'invoices'}
              </p>

              <p className="text-[10px] text-slate-400">
                {activeClient?.name}
              </p>
            </div>
          </section>
        </motion.div>
      )}

      {/* FIXED ACTION MENU */}

      {openActionMenu &&
        (() => {
          const invoice = invoices.find(
            (item) => item.id === openActionMenu
          );

          if (!invoice) {
            return null;
          }

          return (
            <div
              ref={actionMenuRef}
              style={{
                top: actionMenuPosition.top,
                left: actionMenuPosition.left
              }}
              className="fixed z-[9999] w-52 overflow-hidden rounded-xl border border-slate-200 bg-white py-1.5 text-left shadow-xl"
            >
              {invoice.status === 'draft' && (
                <button
                  type="button"
                  onClick={() =>
                    handleOpenSendModal(invoice)
                  }
                  className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-xs text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                >
                  <SafeIcon
                    icon={FiSend}
                    className="shrink-0 text-slate-400"
                  />

                  Send to customer
                </button>
              )}

              {canResendInvoice(invoice) && (
                <button
                  type="button"
                  onClick={() =>
                    handleOpenResendModal(invoice)
                  }
                  disabled={busy}
                  className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-xs text-slate-600 transition hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
                >
                  <SafeIcon
                    icon={FiRefreshCw}
                    className="shrink-0 text-slate-400"
                  />

                  Resend Invoice Email
                </button>
              )}

              {invoice.hosted_invoice_url && (
                <button
                  type="button"
                  onClick={() =>
                    handleOpenInvoice(invoice)
                  }
                  className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-xs text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                >
                  <SafeIcon
                    icon={FiExternalLink}
                    className="shrink-0 text-slate-400"
                  />

                  Open invoice
                </button>
              )}

              <button
                type="button"
                onClick={() =>
                  handleDownloadPDF(invoice)
                }
                className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-xs text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
              >
                <SafeIcon
                  icon={FiDownload}
                  className="shrink-0 text-slate-400"
                />

                {invoice.invoice_pdf_url
                  ? 'Invoice PDF'
                  : 'Print invoice'}
              </button>

              {invoice.status === 'draft' && (
                <>
                  <div className="my-1 border-t border-slate-100" />

                  <button
                    type="button"
                    onClick={() =>
                      handleDelete(invoice)
                    }
                    className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-xs text-rose-600 transition hover:bg-rose-50"
                  >
                    <SafeIcon
                      icon={FiTrash2}
                      className="shrink-0"
                    />

                    Delete draft
                  </button>
                </>
              )}
            </div>
          );
        })()}

      {/* RESEND INVOICE CONFIRMATION MODAL */}

      {resendModalInvoice && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/40 px-4 backdrop-blur-[2px]"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              handleCloseResendModal();
            }
          }}
        >
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.97,
              y: 8
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }}
            transition={{ duration: 0.16 }}
            className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
          >
            {/* HEADER */}

            <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <SafeIcon
                    icon={FiRefreshCw}
                    className="text-base"
                  />
                </div>

                <div>
                  <h2 className="text-base font-semibold text-slate-900">
                    Resend invoice email?
                  </h2>

                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Send another email reminder with the
                    existing invoice payment link.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleCloseResendModal}
                disabled={busy}
                aria-label="Close"
                className="ml-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>

            {/* DETAILS */}

            <div className="px-6 py-5">
              <div className="rounded-xl border border-slate-200 bg-slate-50/70">
                <div className="border-b border-slate-200 px-4 py-3.5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
                    Invoice
                  </p>

                  <p className="mt-1 text-sm font-semibold text-slate-900">
                    {resendModalInvoice.invoice_number}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 px-4 py-3.5">
                  <div>
                    <p className="text-[10px] font-semibold uppercase text-slate-400">
                      Customer
                    </p>

                    <p className="mt-1 truncate text-xs font-medium text-slate-800">
                      {resendModalInvoice.customer?.name ||
                        'Customer'}
                    </p>
                  </div>

                  <div>
                    <p className="text-[10px] font-semibold uppercase text-slate-400">
                      Amount Due
                    </p>

                    <p className="mt-1 text-xs font-semibold text-slate-900">
                      {formatAmount(
                        resendModalInvoice.amount_due,
                        resendModalInvoice.currency ||
                          defaultCurrency
                      )}
                    </p>
                  </div>

                  <div className="col-span-2">
                    <p className="text-[10px] font-semibold uppercase text-slate-400">
                      Recipient Email
                    </p>

                    <p className="mt-1 break-all text-xs text-slate-700">
                      {resendModalInvoice.customer?.email}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-blue-50 px-3.5 py-3">
                <SafeIcon
                  icon={FiAlertCircle}
                  className="mt-0.5 shrink-0 text-blue-500"
                />

                <p className="text-[11px] leading-5 text-blue-700">
                  This sends an email reminder only.
                  No new invoice or payment will be created,
                  and the invoice status will remain unchanged.
                </p>
              </div>
            </div>

            {/* FOOTER */}

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-4">
              <button
                type="button"
                onClick={handleCloseResendModal}
                disabled={busy}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-xs font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmResendInvoice}
                disabled={busy}
                className="inline-flex min-w-[145px] items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-xs font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <SafeIcon
                  icon={
                    resendingInvoiceId
                      ? FiLoader
                      : FiSend
                  }
                  className={
                    resendingInvoiceId
                      ? 'animate-spin'
                      : ''
                  }
                />

                {resendingInvoiceId
                  ? 'Resending...'
                  : 'Resend Email'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* SEND INVOICE CONFIRMATION MODAL */}

      {sendModalInvoice && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/40 px-4 backdrop-blur-[2px]"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget &&
              !busy
            ) {
              handleCloseSendModal();
            }
          }}
        >
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.97,
              y: 8
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }}
            transition={{ duration: 0.16 }}
            className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
          >
            {/* MODAL HEADER */}

            <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <SafeIcon
                    icon={FiSend}
                    className="text-base"
                  />
                </div>

                <div>
                  <h2 className="text-base font-semibold text-slate-900">
                    Send invoice?
                  </h2>

                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Stripe will finalize the invoice and
                    generate a secure payment link.
                    Email delivery requires SendGrid
                    to be configured.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleCloseSendModal}
                disabled={busy}
                aria-label="Close"
                className="ml-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>

            {/* MODAL BODY */}

            <div className="px-6 py-5">
              <div className="rounded-xl border border-slate-200 bg-slate-50/70">
                <div className="border-b border-slate-200 px-4 py-3.5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
                    Invoice
                  </p>

                  <p className="mt-1 text-sm font-semibold text-slate-900">
                    {sendModalInvoice.invoice_number}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 px-4 py-3.5">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                      Customer
                    </p>

                    <p className="mt-1 truncate text-xs font-medium text-slate-800">
                      {sendModalInvoice.customer?.name ||
                        'Customer'}
                    </p>
                  </div>

                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                      Amount
                    </p>

                    <p className="mt-1 text-xs font-semibold text-slate-900">
                      {formatAmount(
                        sendModalInvoice.total_amount,
                        sendModalInvoice.currency ||
                          defaultCurrency
                      )}
                    </p>
                  </div>

                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                      Email
                    </p>

                    <p className="mt-1 truncate text-xs text-slate-700">
                      {sendModalInvoice.customer?.email ||
                        'N/A'}
                    </p>
                  </div>

                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                      Due
                    </p>

                    <p className="mt-1 text-xs text-slate-700">
                      {sendModalInvoice.due_date
                        ? format(
                            new Date(
                              `${sendModalInvoice.due_date}T00:00:00`
                            ),
                            'MMM dd, yyyy'
                          )
                        : 'No due date'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-blue-50 px-3.5 py-3">
                <SafeIcon
                  icon={FiAlertCircle}
                  className="mt-0.5 shrink-0 text-blue-500"
                />

                <p className="text-[11px] leading-5 text-blue-700">
                  Once finalized, this invoice can no longer
                  be edited as a draft. You can resend an
                  email reminder while it remains unpaid.
                </p>
              </div>
            </div>

            {/* MODAL FOOTER */}

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-4">
              <button
                type="button"
                onClick={handleCloseSendModal}
                disabled={busy}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-xs font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmSendInvoice}
                disabled={busy}
                className="inline-flex min-w-[118px] items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-xs font-medium text-white shadow-sm transition hover:bg-slate-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
              >
                <SafeIcon
                  icon={
                    sendingInvoiceId
                      ? FiLoader
                      : FiSend
                  }
                  className={
                    sendingInvoiceId
                      ? 'animate-spin'
                      : ''
                  }
                />

                {sendingInvoiceId
                  ? 'Sending...'
                  : 'Send Invoice'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}